// ============================================================================
// JARVISTRAVEL - FATIGUE INDEX ENGINE
// Pure functions that rate a day's fatigue, explain it, detect booked-item
// ("anchor") conflicts when days/activities are moved, and re-balance an
// over-packed trip by redistributing flexible activities to lighter days.
//
// A day's rating is the calibrated Fatigue Index (utils/fatigueIndex.ts, a port
// of Core's formula), or Core's own rating of the same day once Core has given
// one (utils/coreRatings.ts). The trip rollup below is still the app's own.
// ============================================================================

import type { Activity, ActivityType, DayShift, ItineraryDay, FatigueLevel } from '@/app/types';
import { clampFi, fiBand, fiBandLabel, FI_MIN, type FIBand } from '@/app/utils/fi';
import { rateDay, rateTrip, type DayItem, type DayRating, type IndexDay, type TravelMode } from '@/app/utils/fatigueIndex';
import { coreRatingFor } from '@/app/utils/coreRatings';
import { formatTime } from '@/app/utils';

// ----------------------------------------------------------------------------
// ANCHORS
// ----------------------------------------------------------------------------

/**
 * An activity is an "anchor" when it's booked or explicitly locked — a flight,
 * hotel, or reserved restaurant that is tied to its date and cannot move
 * without re-booking.
 */
export const isAnchored = (a: Activity): boolean => a.locked === true || a.booked === true;

/** Anchored activities present in a day. */
export const anchorsIn = (day: ItineraryDay): Activity[] => day.activities.filter(isAnchored);

// ----------------------------------------------------------------------------
// SCORING
// ----------------------------------------------------------------------------

// The public 1–9 scale starts the "Packed" band at 7 (see utils/fi.ts). An
// over-packed day (≥7) is what a move is warned about, and what core's
// re-balance works on.
export const PACKED_THRESHOLD = 7;

const TRAVEL_MODE: Partial<Record<ActivityType, TravelMode>> = { flight: 'flight', transport: 'transfer' };
const REST_TYPES: ReadonlySet<ActivityType> = new Set<ActivityType>(['rest', 'wellness']);
// An item with no time sits at midday, so it counts its load but takes nothing
// from the night. An item with no duration counts as an hour, the usual length
// of a calendar entry; one saying 0 minutes is kept at 0.
const UNTIMED_START = 12 * 60;
const UNKNOWN_DURATION = 60;
const LAST_MINUTE = 24 * 60 - 1;

const durationOf = (a: Activity): number => Math.min(LAST_MINUTE, Math.max(0, a.duration ?? UNKNOWN_DURATION));

const clock = (minutes: number): string =>
  `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** A day in the formula's terms: Core's wire shape, the same for the port and
 *  for `POST /v1/fatigue/rate`. `flight`/`transport` are travel, `rest`/`wellness`
 *  rest, and everything else an activity at its chill/balanced/packed tag. */
export function indexDayOf(activities: Activity[], shift?: DayShift): IndexDay {
  const items = activities.map((a): DayItem => {
    const at = timeToMinutes(a.time);
    const start = at < 0 ? UNTIMED_START : at;
    const span = { start: clock(start), end: clock(start + durationOf(a)) };
    const mode = TRAVEL_MODE[a.type];
    if (mode) return { kind: 'travel', mode, ...span };
    if (REST_TYPES.has(a.type)) return { kind: 'rest', ...span };
    // An unrecognised tag reads as balanced rather than failing the whole day.
    const intensity = a.fatigueImpact === 'chill' || a.fatigueImpact === 'packed' ? a.fatigueImpact : 'balanced';
    return { kind: 'activity', intensity, ...span };
  });
  const hours = Number.isFinite(shift?.hours) ? (shift as DayShift).hours : 0;
  const daysSince = Number.isFinite(shift?.daysSince) ? Math.max(0, Math.floor((shift as DayShift).daysSince)) : 0;
  return { tz_shift_hours: hours, days_since_shift: daysSince, items };
}

/** The day's full rating: Core's, when Core has rated this exact day, else the
 *  device's own from the same formula. */
export function rateDayOf(activities: Activity[], shift?: DayShift): DayRating {
  const day = indexDayOf(activities, shift);
  return coreRatingFor(day) ?? rateDay(day);
}

/** Public 1–9 Fatigue Index for a single day. An empty day is the calmest (1).
 *  Bands (utils/fi.ts): 1–3 Chill · 4–6 Balanced · 7–9 Packed. */
export function computeDayScore(activities: Activity[], shift?: DayShift): number {
  // Runtime guard: a malformed day (activities missing entirely) reads as the
  // calm floor instead of crashing every FI surface — but loudly, so the
  // upstream data bug is visible rather than masked.
  if (!Array.isArray(activities)) {
    console.warn('[fiEngine] computeDayScore received malformed activities:', activities);
    return FI_MIN;
  }
  return rateDayOf(activities, shift).fi;
}

/** Map a 1–9 score to its band label — single source of truth in utils/fi.ts. */
export const levelFromScore = (score: number): FatigueLevel =>
  fiBandLabel(fiBand(score)) as FatigueLevel;

export interface DayFI {
  score: number;
  level: FatigueLevel;
}

export const computeDayFI = (activities: Activity[], shift?: DayShift): DayFI => {
  const score = computeDayScore(activities, shift);
  return { score, level: levelFromScore(score) };
};

/** Refresh fiScore / fiLevel on every day from its current activities. */
export function recomputeDays(days: ItineraryDay[]): ItineraryDay[] {
  return days.map((d) => {
    const { score, level } = computeDayFI(d.activities, d.tzShift);
    return { ...d, fiScore: score, fiLevel: level };
  });
}

/** A day's rating on the 1-9 scale: the stored score when the day has one, else
 *  the engine's. The alert, the pacing strip, trip stats, the share document and
 *  the over-packed check all read it here, so they agree on a day. The day
 *  badges show only a stored score, so they skip the fallback (JAR-1442 review). */
export const dayScore = (day: ItineraryDay): number =>
  clampFi(day.fiScore ?? computeDayScore(day.activities, day.tzShift));

/** The days re-rated, when a saved score no longer matches the rating the app now
 *  shows — which is what happens when Core's rating arrives and differs from the
 *  device's. Null when every saved score is current, so a caller can skip the
 *  update entirely (JAR-1678). */
export function refreshStaleScores(days: ItineraryDay[]): ItineraryDay[] | null {
  const stale = days.some((d) => d.fiScore !== undefined && d.fiScore !== computeDayScore(d.activities, d.tzShift));
  return stale ? recomputeDays(days) : null;
}

/** Days rated at or above the packed threshold, by the same rating the app shows. */
export const overPackedDays = (days: ItineraryDay[]): ItineraryDay[] =>
  days.filter((d) => dayScore(d) >= PACKED_THRESHOLD);

// ----------------------------------------------------------------------------
// V4 DAY EXPLANATION  ("why this day feels the way it does")
// ----------------------------------------------------------------------------
// Front-end stand-in for the FIS V4 `score-day` response. Emits the public
// score + band plus PLAIN-LANGUAGE drivers / mitigations / recovery windows —
// never hidden-state variable names (FIS FE spec §12.2). A real `/score-day`
// endpoint (JAR-135) drops in behind this shape without touching callers.

/** Context that sharpens the explanation (e.g. the prior day for back-to-back
 *  heavy days). All optional — the heuristic degrades gracefully without it. */
export interface DayContext {
  prevDay?: ItineraryDay;
}

/** The V4 day payload — matches `examples/fis_v4_score_day_response.json`. */
export interface DayScore {
  fi_day: number;          // 1–9 public score
  band: FIBand;            // chill | balanced | packed
  top_drivers: string[];   // why it feels this way, in human language
  mitigations: string[];   // what would help (substantive only when it's heavy)
  recovery: string[];      // protected recovery windows detected in the day
}

const HOUR = 60;

/** Signals we can read straight off the itinerary — the only inputs the
 *  front-end heuristic has until the real engine lands. */
function daySignals(activities: Activity[]) {
  const sorted = sortByTime(activities);
  const mins = (a?: Activity) => timeToMinutes(a?.time);
  const totalMins = activities.reduce((s, a) => s + (a.duration ?? 0), 0);
  const last = sorted[sorted.length - 1];
  const lastStart = mins(last);
  let tightGaps = 0;
  let biggestGap = 0;
  for (let i = 1; i < sorted.length; i++) {
    const prevEnd = mins(sorted[i - 1]) + (sorted[i - 1].duration ?? 0);
    const nextStart = mins(sorted[i]);
    if (prevEnd < 0 || nextStart < 0) continue;
    const gap = nextStart - prevEnd;
    if (gap < 30) tightGaps++;
    if (gap > biggestGap) biggestGap = gap;
  }
  return {
    count: activities.length,
    packed: activities.filter((a) => a.fatigueImpact === 'packed').length,
    chill: activities.filter((a) => a.fatigueImpact === 'chill').length,
    rest: activities.filter((a) => a.type === 'rest' || a.type === 'wellness').length,
    longBlocks: activities.filter((a) => (a.duration ?? 0) >= 150).length,
    hasTravel: activities.some((a) => a.type === 'flight' || a.type === 'transport'),
    hours: totalMins / HOUR,
    firstStart: mins(sorted[0]),
    lastEnd: lastStart >= 0 ? lastStart + (last?.duration ?? 0) : -1,
    tightGaps,
    biggestGap,
  };
}

/** Recovery windows: explicit rest/wellness blocks + any long open gap. */
function recoveryWindows(activities: Activity[]): string[] {
  const out: string[] = [];
  for (const a of activities) {
    // Name fallback: an unnamed block must never render as "undefined · 2:00 PM".
    if (a.type === 'rest' || a.type === 'wellness') out.push(`${a.name || 'Rest period'}${a.time ? ` · ${formatTime(a.time)}` : ''}`);
  }
  const s = daySignals(activities);
  if (s.biggestGap >= 90) out.push(`${Math.round(s.biggestGap / HOUR)}h of open time mid-day`);
  return out.slice(0, 3);
}

const QUARTER_GLYPHS = ['', '¼', '½', '¾'];

/** The hours in "adjusting to … -hour time change": to the nearest quarter, with
 *  its fraction as people write it, after the article the number is said with:
 *  "a 5½", "a 6", "an 8", "an 11¼" (JAR-3317). Core's score_day.go ports it. */
function hoursWithArticle(h: number): string {
  const q = Math.round(h * 4);
  const whole = Math.floor(q / 4);
  const glyph = QUARTER_GLYPHS[q % 4];
  const n = whole > 0 || !glyph ? `${whole}${glyph}` : glyph;
  // Said "eight", "eleven", "eighteen".
  return `${whole === 8 || whole === 11 || whole === 18 ? 'an' : 'a'} ${n}`;
}

/**
 * Score a day and explain it. The explanation is ranked by how much each signal
 * shapes the day, then capped — so the card stays scannable.
 */
export function scoreDay(day: ItineraryDay, ctx: DayContext = {}): DayScore {
  // Normalize once — daySignals/recoveryWindows/etc. all assume an array (M2).
  const acts = Array.isArray(day.activities) ? day.activities : [];
  const { fi: fi_day, factors } = rateDayOf(acts, day.tzShift);
  const band = fiBand(fi_day);
  const s = daySignals(acts);
  const prevPacked = ctx.prevDay ? computeDayScore(ctx.prevDay.activities, ctx.prevDay.tzShift) >= PACKED_THRESHOLD : false;
  const shiftLeft = day.tzShift ? Math.max(0, Math.abs(day.tzShift.hours) - day.tzShift.daysSince) : 0;

  const drivers: string[] = [];
  const add = (cond: boolean, text: string) => { if (cond && drivers.length < 3) drivers.push(text); };

  if (band === 'chill') {
    add(true, 'a relaxed pace with room to breathe');
    add(s.rest > 0, 'time set aside to rest');
    add(s.count === 0, 'nothing scheduled, a true rest day');
    add(s.count >= 1 && s.count <= 2, 'only a couple of things on');
    add(drivers.length === 0, 'plenty of open time');
  } else {
    // The formula's own signals lead, so the explanation names what moved the number.
    add(factors.travel >= 0.5, 'a long day of travel');
    add(factors.jet_lag >= 0.25, `adjusting to ${hoursWithArticle(shiftLeft)}-hour time change`);
    add(factors.sleep > 0, 'a short night, with plans outside 8 AM to 10 PM');
    add(s.packed >= 3, 'several high-energy stops back to back');
    add(prevPacked && band === 'packed', 'it lands right after a heavy day');
    add(s.count >= 6, `a full schedule: ${s.count} stops`);
    add(s.hours >= 7, `around ${Math.round(s.hours)} hours on the go`);
    add(s.firstStart >= 0 && s.firstStart < 8 * HOUR, 'an early start');
    add(s.lastEnd >= 21 * HOUR, 'a late finish');
    add(s.tightGaps >= 2, 'little downtime between stops');
    add(s.rest === 0 && band === 'packed', 'no real break built in');
    add(s.longBlocks >= 1, 'a long block in the middle of the day');
    add(s.hasTravel && factors.travel < 0.5, 'travel in the mix');
    add(drivers.length === 0, band === 'packed' ? 'a demanding stretch of activities' : 'a good mix of activity and downtime');
  }

  const mitigations: string[] = [];
  const tip = (cond: boolean, text: string) => { if (cond && mitigations.length < 3) mitigations.push(text); };
  if (band === 'packed') {
    tip(s.rest === 0, 'add a short break in the afternoon');
    tip(s.packed >= 3, 'swap one high-energy stop for something easy');
    tip(factors.sleep > 0 || (s.firstStart >= 0 && s.firstStart < 8 * HOUR && s.lastEnd >= 21 * HOUR), 'start a little later or wrap up earlier');
    tip(s.tightGaps >= 2, 'leave a little space between stops');
    tip(s.longBlocks >= 1, 'trim or split a long block');
    tip(s.count >= 6, 'move one stop to a lighter day');
    tip(mitigations.length === 0, 'shift one thing to a calmer day');
  } else if (band === 'balanced') {
    tip(s.lastEnd >= 21 * HOUR, 'keep the evening easy to stay balanced');
    tip(true, 'looks manageable, with room for a spontaneous stop');
  } else {
    tip(true, 'lots of breathing room, a good day to add something if you’d like');
  }

  return { fi_day, band, top_drivers: drivers, mitigations, recovery: recoveryWindows(acts) };
}

/** Trip-level rollup of the day scores — the number plus a plain-language read
 *  on the trip's overall rhythm. Front-end stand-in for the trip FI (the real
 *  CalculateTripFI compounding lives in `core`). */
export interface TripScore {
  fi: number;
  band: FIBand;
  heavyDays: ItineraryDay[];
  top_drivers: string[];
  mitigations: string[];
  /** No day has an activity yet. `fi` is then the scale's floor, not a rating,
   *  so surfaces show "Nothing planned yet" instead of a number (JAR-1451). */
  nothingPlanned?: boolean;
  /** A run of packed days, or a trip that rates Packed, said plainly (JAR-1687). */
  warning?: string;
}

// The warnings Core's trip rollup gave, read from the rating this app computes.
// The rollup read logs nothing writes, so it only ever said there was no data
// (JAR-1687).
function tripWarning(packedRun: number, band: FIBand): string | undefined {
  if (packedRun >= 3) return 'Three or more packed days in a row. A rest day would help.';
  if (packedRun === 2) return 'Two packed days in a row. A lighter day would help.';
  if (band === 'packed') return 'This trip rates Packed overall. Plan some rest.';
  return undefined;
}

export function explainTrip(days: ItineraryDay[]): TripScore {
  const scored = days
    // A malformed day (no activities array) is skipped like an empty one, the way
    // computeDayScore guards it, rather than throwing in every caller's render.
    .filter((d) => Array.isArray(d.activities) && d.activities.length > 0)
    .map((d) => ({ d, s: computeDayScore(d.activities, d.tzShift) }));

  if (scored.length === 0) {
    return { fi: FI_MIN, band: 'chill', heavyDays: [], top_drivers: ['nothing planned yet'], mitigations: ['start adding days and the pace fills in'], nothingPlanned: true };
  }

  // The trip's rating is its typical day, with a run of packed days compounding
  // it (utils/fatigueIndex.ts, Core's RateTrip). A day with nothing on it is not
  // a chill day, it is an unplanned one, so it stays out of the reckoning and the
  // heavy days are named below instead.
  const { fi, packed_run } = rateTrip(scored.map((x) => x.s));
  const band = fiBand(fi);
  const heavyDays = scored.filter((x) => x.s >= PACKED_THRESHOLD).map((x) => x.d);
  const chillN = scored.filter((x) => x.s <= 3).length;

  const top_drivers: string[] = [];
  if (heavyDays.length) top_drivers.push(`${heavyDays.length} ${heavyDays.length === 1 ? 'day runs' : 'days run'} heavy`);
  if (chillN) top_drivers.push(`${chillN} easy ${chillN === 1 ? 'day' : 'days'} to recover`);
  if (heavyDays.length === 0) top_drivers.push('a well-paced trip overall');

  const mitigations: string[] = heavyDays.length
    ? [heavyDays.length === 1 ? 'rebalance the heavy day to even things out' : 'rebalance the heaviest days to even things out']
    : ['nice rhythm: keep a few evenings open'];

  return { fi, band, heavyDays, top_drivers, mitigations, warning: tripWarning(packed_run, band) };
}

// ----------------------------------------------------------------------------
// HELPERS
// ----------------------------------------------------------------------------

/** Parse an "H:MM" / "HH:MM" time label to minutes since midnight; -1 if absent
 *  or unparseable. Numeric so "9:30" sorts before "10:00" (string compare doesn't). */
export const timeToMinutes = (t?: string): number => {
  const m = /^(\d{1,2}):(\d{2})/.exec((t || '').trim());
  if (!m) return -1;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return -1; // reject impossible clock values (e.g. "25:00")
  return h * 60 + min;
};

const timeKey = (t?: string) => {
  const m = timeToMinutes(t);
  return m < 0 ? Number.MAX_SAFE_INTEGER : m;
};

/** Activities sorted by their time label (numeric, so un-padded times are safe).
 *  Untimed activities sort LAST — leading a day with "sometime" items before
 *  the 9:00 start reads as broken ordering. */
export const sortByTime = (activities: Activity[]): Activity[] =>
  [...activities].sort((a, b) => timeKey(a.time) - timeKey(b.time));

/** Where addActivity puts `activity` among a day's `activities`: after every
 *  stop whose time sorts at or before its own, since the sort is stable and the
 *  new stop goes in last. Counted, so it can't miss (JAR-1913). */
export const addedPlace = (activities: Activity[], activity: Activity): number =>
  activities.filter((a) => timeKey(a.time) <= timeKey(activity.time)).length;

const cloneDays = (days: ItineraryDay[]): ItineraryDay[] =>
  days.map((d) => ({ ...d, activities: [...d.activities] }));

// ----------------------------------------------------------------------------
// DAY SWAPPING
// ----------------------------------------------------------------------------

/**
 * Reorder days by moving one day to a new position (insertion, not a two-slot
 * swap): the WHOLE day's content shifts to `to`, and the days in between slide
 * over by one. `date` / `dayNumber` / `id` are slot identity and stay put, so
 * "drag day C above B" makes C the day-2 slot and B the day-3 slot. Booked
 * items travel with the day and may need re-dating — the caller prompts via
 * Jarvis (see bookingsDisplacedByReorder).
 */
export function reorderDays(days: ItineraryDay[], from: number, to: number): ItineraryDay[] {
  if (from === to || from < 0 || to < 0 || from >= days.length || to >= days.length) return recomputeDays(days);
  const next = cloneDays(days);
  // Content = what a day *is* (its activities + per-day notes/location).
  const content = next.map((d) => ({ activities: d.activities, location: d.location, notes: d.notes }));
  const [moved] = content.splice(from, 1);
  content.splice(to, 0, moved);
  next.forEach((slot, i) => {
    slot.activities = content[i].activities;
    slot.location = content[i].location;
    slot.notes = content[i].notes;
  });
  return recomputeDays(next);
}

/**
 * Booked activities whose date would change if the days were reordered `from`→
 * `to` — the anchors a whole-day move re-dates (so the caller can offer to keep
 * them booked vs re-book). Empty ⇒ the reorder is clean.
 */
export function bookingsDisplacedByReorder(days: ItineraryDay[], from: number, to: number): Activity[] {
  if (from === to) return [];
  const after = reorderDays(days, from, to);
  const displaced: Activity[] = [];
  for (const day of after) {
    for (const a of day.activities) {
      if (!isAnchored(a)) continue;
      const prev = days.find((d) => d.activities.some((x) => x.id === a.id));
      if (prev && prev.date !== day.date) displaced.push(a);
    }
  }
  return displaced;
}

// ----------------------------------------------------------------------------
// ACTIVITY MOVING
// ----------------------------------------------------------------------------

/**
 * Move a single activity to another day. If `beforeId` is given the activity is
 * inserted right before that target (so the user places it exactly where they
 * dropped it); otherwise it's appended in time order.
 */
export function moveActivity(
  days: ItineraryDay[],
  activityId: string,
  fromDayId: string,
  toDayId: string,
  beforeId?: string,
): ItineraryDay[] {
  if (fromDayId === toDayId) return recomputeDays(days);
  const next = cloneDays(days);
  const from = next.find((d) => d.id === fromDayId);
  const to = next.find((d) => d.id === toDayId);
  if (!from || !to) return recomputeDays(days);
  const act = from.activities.find((a) => a.id === activityId);
  if (!act) return recomputeDays(days);
  from.activities = from.activities.filter((a) => a.id !== activityId);
  const beforeIdx = beforeId ? to.activities.findIndex((a) => a.id === beforeId) : -1;
  to.activities = beforeIdx >= 0
    ? [...to.activities.slice(0, beforeIdx), act, ...to.activities.slice(beforeIdx)]
    : sortByTime([...to.activities, act]);
  return recomputeDays(next);
}

/**
 * Reorder a flexible activity to another activity's slot WITHIN the same day
 * (manual order, not re-sorted by time — so the user can see, and we can flag,
 * an out-of-sequence placement). Anchors are pinned and never moved here.
 */
export function reorderWithinDay(days: ItineraryDay[], dayId: string, activeId: string, overId: string): ItineraryDay[] {
  if (activeId === overId) return days;
  const next = cloneDays(days);
  const day = next.find((d) => d.id === dayId);
  if (!day) return days;
  const moved = day.activities.find((a) => a.id === activeId);
  if (!moved) return days;
  const without = day.activities.filter((a) => a.id !== activeId);
  const toIdx = without.findIndex((a) => a.id === overId);
  if (toIdx < 0) return days;
  without.splice(toIdx, 0, moved); // insert right before the target ("in between")
  day.activities = without;
  return next;
}

/** Save a stop as its editor does: patch its fields, and on a new time move it
 *  to just after the last other stop timed at or before it, which on a day in
 *  time order is where addActivity would put it. The rest keep their places, so
 *  an order someone dragged stays theirs (JAR-2013). */
export function editActivity(days: ItineraryDay[], dayId: string, activityId: string, patch: Partial<Activity>): ItineraryDay[] {
  const next = cloneDays(days);
  const day = next.find((d) => d.id === dayId);
  if (!day) return days;
  const current = day.activities.find((a) => a.id === activityId);
  if (current) {
    const updated = { ...current, ...patch };
    if (patch.time !== undefined && timeKey(patch.time) !== timeKey(current.time)) {
      const others = day.activities.filter((a) => a.id !== activityId);
      const key = timeKey(updated.time);
      const at = others.map((a) => timeKey(a.time) <= key).lastIndexOf(true) + 1;
      day.activities = [...others.slice(0, at), updated, ...others.slice(at)];
    } else {
      day.activities = day.activities.map((a) => (a.id === activityId ? updated : a));
    }
  }
  return recomputeDays(next);
}

/** Remove a single activity from its day (recomputing the day's FI). */
export function removeActivity(days: ItineraryDay[], dayId: string, activityId: string): ItineraryDay[] {
  const next = cloneDays(days);
  const day = next.find((d) => d.id === dayId);
  if (!day) return days;
  day.activities = day.activities.filter((a) => a.id !== activityId);
  return recomputeDays(next);
}

/** Add an activity to a day (in time order), recomputing the day's FI. Used when
 *  a booked experience from Explore is applied to a chosen day. */
export function addActivity(days: ItineraryDay[], dayId: string, activity: Activity): ItineraryDay[] {
  const next = cloneDays(days);
  const day = next.find((d) => d.id === dayId);
  if (!day) return days;
  day.activities = sortByTime([...day.activities, activity]);
  return recomputeDays(next);
}

export interface ChronoConflict {
  /** The flexible activity that's out of sequence. */
  name: string;
  /** The booked anchor it conflicts with. */
  anchor: string;
}

/**
 * Detect out-of-sequence placements relative to booked anchors: an activity
 * ordered before an anchor but starting later (e.g. dragged before the arrival
 * flight), or after an anchor but starting earlier (e.g. after departure).
 * Returns EVERY such conflict — a pass that fixed only the first inversion
 * used to leave the rest undetected.
 */
export function chronoConflicts(activities: Activity[]): ChronoConflict[] {
  const out: ChronoConflict[] = [];
  for (let i = 0; i < activities.length; i++) {
    const a = activities[i];
    const ta = timeToMinutes(a.time);
    if (ta < 0) continue;
    for (let j = i + 1; j < activities.length; j++) {
      const b = activities[j];
      const tb = timeToMinutes(b.time);
      if (tb < 0) continue;
      // a is ordered before b but starts later → inversion; flag if an anchor is involved.
      if (ta > tb && (isAnchored(a) || isAnchored(b))) {
        const anchor = isAnchored(a) ? a : b;
        const other = isAnchored(a) ? b : a;
        // Name fallbacks — a nameless activity must never render "undefined".
        out.push({ name: other.name || 'An activity', anchor: anchor.name || 'a confirmed item' });
      }
    }
  }
  return out;
}

/** First conflict only — convenience for single-message call sites. */
export function chronoConflict(activities: Activity[]): ChronoConflict | null {
  return chronoConflicts(activities)[0] ?? null;
}

/**
 * Stops on one day that overlap, each with a plain-words line naming the other
 * (JAR-2013): the same start time, or a start inside the other's duration,
 * which is an hour when a stop gives none, as the day's score counts it. Both
 * stops are flagged, and a stop's first clash is the one it shows. Only a stay
 * is left out, since its hours run through the evening every other stop fills,
 * and an untimed stop can't clash. Times are read on the day's own clock: a stop
 * at 00:30 sorts first, so a late stop running past midnight isn't flagged
 * against it. Keyed by activity id.
 */
/** Whether two stops share anyone going, so their overlap is a real clash
 *  (JAR-2014). An empty list means everyone, so a stop with nobody picked
 *  shares with every other stop. */
function sharesMember(a?: string[], b?: string[]): boolean {
  if (!a?.length || !b?.length) return true;
  const seen = new Set(a);
  return b.some((id) => seen.has(id));
}

export function stopClashes(activities: Activity[]): Map<string, string> {
  const out = new Map<string, string>();
  const note = (a: Activity, line: string) => { if (!out.has(a.id)) out.set(a.id, line); };
  const label = (a: Activity) => a.name || 'another stop';
  const timed = sortByTime(activities.filter((a) => a.type !== 'accommodation' && timeToMinutes(a.time) >= 0));
  for (let i = 0; i < timed.length; i++) {
    const first = timed[i];
    const start = timeToMinutes(first.time);
    for (let j = i + 1; j < timed.length; j++) {
      const second = timed[j];
      const next = timeToMinutes(second.time);
      // Two overlapping stops are fine when no one is on both — a shared trip
      // lets people do different things at once (JAR-2014). A stop with nobody
      // picked means everyone, so it still clashes with all.
      if (!sharesMember(first.going, second.going)) continue;
      if (next === start) {
        note(first, `Same time as ${label(second)}`);
        note(second, `Same time as ${label(first)}`);
      } else if (next < start + durationOf(first)) {
        note(first, `Runs into ${label(second)}`);
        note(second, `Starts during ${label(first)}`);
      }
    }
  }
  return out;
}

